import {useState} from 'react';
import Icon from '../../../components/AppIcon';
import * as LucideIcons from "lucide-react";

const SecuritySection = () => {
    const [activeFeature, setActiveFeature] = useState('encryption');

    const securityFeatures = [
        {
            id: 'encryption',
            icon: 'Shield',
            title: 'End-to-End Encryption',
            description: 'Military-grade AES-256 encryption protects all transactions and data',
            details: [
                'AES-256 encryption for data at rest and in transit',
                'TLS 1.3 secure communication protocols',
                'Zero-knowledge architecture for sensitive data',
                'Encrypted backup and disaster recovery systems',
                'Regular security audits and penetration testing'
            ],
            compliance: ['ISO 27001', 'PCI DSS Level 1', 'SOC 2 Type II']
        },
        {
            id: 'authentication',
            icon: 'Fingerprint',
            title: 'Multi-Factor Authentication',
            description: 'Biometric and multi-layer authentication for secure access',
            details: [
                'Fingerprint and facial recognition for collectors',
                'SMS and email two-factor authentication',
                'Ghana Card integration for citizen verification',
                'Role-based access control (RBAC)',
                'Session management and automatic logout'
            ],
            compliance: ['NIST Cybersecurity Framework', 'Ghana Data Protection Act']
        },
        {
            id: 'banking',
            icon: 'Building2',
            title: 'Banking Integration',
            description: 'Direct integration with Ghana Commercial Bank and major financial institutions',
            details: [
                'Real-time settlement with GCB and other banks',
                'Automated reconciliation and reporting',
                'Fraud detection and prevention systems',
                'Anti-money laundering (AML) compliance',
                'Central Bank of Ghana regulatory compliance'
            ],
            compliance: ['Bank of Ghana Regulations', 'Payment Systems Act 2003']
        },
        {
            id: 'audit',
            icon: 'FileCheck',
            title: 'Immutable Audit Trail',
            description: 'Blockchain-secured transaction records for complete transparency',
            details: [
                'Blockchain-based transaction logging',
                'Tamper-proof audit trails and records',
                'Real-time compliance monitoring',
                'Automated regulatory reporting',
                'Digital forensics capabilities'
            ],
            compliance: ['Ghana Data Protection Act', 'Public Financial Management Act']
        }
    ];

    const certifications = [
        {
            name: 'Ghana Data Protection Act',
            icon: 'Shield',
            status: 'Certified',
            description: 'Full compliance with Ghana\'s data protection regulations'
        },
        {
            name: 'ISO 27001',
            icon: 'Award',
            status: 'Certified',
            description: 'International standard for information security management'
        },
        {
            name: 'PCI DSS Level 1',
            icon: 'CreditCard',
            status: 'Certified',
            description: 'Highest level of payment card industry security'
        },
        {
            name: 'SOC 2 Type II',
            icon: 'CheckCircle',
            status: 'Certified',
            description: 'Security, availability, and confidentiality controls'
        }
    ];

    const currentFeature = securityFeatures?.find(f => f?.id === activeFeature);

    return (
        <section className="py-20 bg-slate-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Section Header */}
                <div className="text-center mb-16">
                    <div
                        className="inline-flex items-center space-x-2 px-4 py-2 bg-success/10 rounded-full border border-success/20 mb-6">
                        <Icon name="Shield" size={16} color="var(--color-success)"/>
                        <span className="text-sm font-medium text-success">Bank-Grade Security</span>
                    </div>

                    <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
                        Security & Compliance You Can Trust
                    </h2>
                    <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                        Built with enterprise-grade security and full compliance with Ghana's regulatory
                        requirements to protect citizen data and government transactions.
                    </p>
                </div>

                <div className="grid lg:grid-cols-2 gap-12">
                    {/* Security Features Navigation */}
                    <div className="space-y-6">
                        <h3 className="text-2xl font-bold text-foreground mb-6">Security Features</h3>

                        <div className="space-y-4">
                            {securityFeatures?.map((feature) => (
                                <button
                                    key={feature?.id}
                                    onClick={() => setActiveFeature(feature?.id)}
                                    className={`w-full text-left p-6 rounded-xl border-2 transition-all duration-300 ${
                                        activeFeature === feature?.id
                                            ? 'border-primary bg-primary/5 shadow-lg'
                                            : 'border-border bg-white hover:border-muted-foreground hover:shadow-md'
                                    }`}
                                >
                                    <div className="flex items-start space-x-4">
                                        <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                                            activeFeature === feature?.id
                                                ? 'bg-primary text-white' : 'bg-muted text-muted-foreground'
                                        }`}>
                                            <Icon name={feature?.icon as keyof typeof LucideIcons} size={24}/>
                                        </div>
                                        <div className="flex-1">
                                            <h4 className={`font-semibold mb-2 ${
                                                activeFeature === feature?.id ? 'text-primary' : 'text-foreground'
                                            }`}>
                                                {feature?.title}
                                            </h4>
                                            <p className="text-sm text-muted-foreground">{feature?.description}</p>
                                        </div>
                                        <Icon
                                            name="ChevronRight"
                                            size={20}
                                            className={`transition-transform ${
                                                activeFeature === feature?.id ? 'rotate-90 text-primary' : 'text-muted-foreground'
                                            }`}
                                        />
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Feature Details */}
                    <div className="bg-white rounded-2xl p-8 shadow-lg">
                        <div className="flex items-center space-x-3 mb-6">
                            <div className="w-16 h-16 bg-primary/10 rounded-xl flex items-center justify-center">
                                <Icon name={currentFeature?.icon as keyof typeof LucideIcons} size={32}
                                      color="var(--color-primary)"/>
                            </div>
                            <div>
                                <h3 className="text-2xl font-bold text-foreground">{currentFeature?.title}</h3>
                                <p className="text-muted-foreground">{currentFeature?.description}</p>
                            </div>
                        </div>

                        {/* Technical Details */}
                        <div className="space-y-6">
                            <div>
                                <h4 className="font-semibold text-foreground mb-4">Technical Implementation</h4>
                                <div className="space-y-3">
                                    {currentFeature?.details?.map((detail, index) => (
                                        <div key={index} className="flex items-start space-x-3">
                                            <Icon name="Check" size={16} color="var(--color-success)"
                                                  className="mt-1 flex-shrink-0"/>
                                            <span className="text-muted-foreground text-sm">{detail}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Compliance Badges */}
                            <div>
                                <h4 className="font-semibold text-foreground mb-4">Compliance Standards</h4>
                                <div className="flex flex-wrap gap-2">
                                    {currentFeature?.compliance?.map((standard, index) => (
                                        <span
                                            key={index}
                                            className="px-3 py-1 bg-success/10 text-success text-sm font-medium rounded-full border border-success/20"
                                        >
                      {standard}
                    </span>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Certifications Grid */}
                <div className="mt-16">
                    <h3 className="text-2xl font-bold text-center text-foreground mb-8">
                        Security Certifications & Compliance
                    </h3>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {certifications?.map((cert, index) => (
                            <div key={index}
                                 className="bg-white rounded-xl p-6 text-center shadow-md border border-border">
                                <div
                                    className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <Icon name={cert?.icon as keyof typeof LucideIcons} size={32}
                                          color="var(--color-success)"/>
                                </div>
                                <h4 className="font-semibold text-foreground mb-2">{cert?.name}</h4>
                                <div className="flex items-center justify-center space-x-1 mb-3">
                                    <Icon name="CheckCircle" size={16} color="var(--color-success)"/>
                                    <span className="text-sm font-medium text-success">{cert?.status}</span>
                                </div>
                                <p className="text-xs text-muted-foreground">{cert?.description}</p>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Security Guarantee */}
                <div className="mt-16 bg-gradient-to-r from-primary/5 to-success/5 rounded-2xl p-8 text-center">
                    <div className="max-w-3xl mx-auto">
                        <div
                            className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
                            <Icon name="ShieldCheck" size={40} color="var(--color-primary)"/>
                        </div>

                        <h3 className="text-2xl font-bold text-foreground mb-4">
                            100% Security Guarantee
                        </h3>
                        <p className="text-lg text-muted-foreground mb-6">
                            We guarantee the security of your data and transactions with our comprehensive
                            insurance coverage and 24/7 security monitoring.
                        </p>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
                            <div>
                                <div className="text-2xl font-bold text-primary mb-2">24/7</div>
                                <p className="text-sm text-muted-foreground">Security Monitoring</p>
                            </div>
                            <div>
                                <div className="text-2xl font-bold text-success mb-2">99.9%</div>
                                <p className="text-sm text-muted-foreground">Uptime Guarantee</p>
                            </div>
                            <div>
                                <div className="text-2xl font-bold text-accent mb-2">₵10M</div>
                                <p className="text-sm text-muted-foreground">Insurance Coverage</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default SecuritySection;